/**
 * BOXO fix: the product video used to autoplay by itself on mobile when a
 * shopper scrolled/swiped to it. After the theme's "Video style" setting
 * was switched to the sound-enabled version, mobile always requires a tap
 * instead (and tapping used to jump the video into a different, fullscreen
 * format).
 *
 * Sound-with-autoplay isn't something any site can do - phone browsers
 * block audio from starting without a tap, no matter how it's coded. So
 * this restores the original behaviour instead: on mobile, whichever slide
 * is the currently-selected one autoplays automatically, muted, in the same
 * square box the poster image uses - no tap, no sound, no fullscreen jump.
 * This mirrors exactly how the desktop version of this same video already
 * autoplays (muted) when its slide is selected.
 *
 * Speed + no glitch (added later):
 *  - The video is loaded in the background right after the page has loaded
 *    (small 480p file), so it is already buffered when the shopper swipes
 *    to it and starts almost instantly.
 *  - The video stays invisible until its first frame is drawn and then fades
 *    in over the sharp still image, so there is no blurry/black flash.
 *
 * It only touches this one interaction; it does not modify or replace any
 * existing theme behaviour (desktop autoplay, thumbnails, zoom, etc.).
 */
(function () {
  // Same breakpoint the theme itself uses to show/hide the mobile vs.
  // desktop video markup ("medium-up--hide" / "small--hide").
  function isMobileWidth() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  var clones = new WeakMap(); // slide element -> the muted video clone in it

  function hideTrigger(slide) {
    var trigger = slide.querySelector('.product-video-trigger--mp4');
    if (trigger) trigger.classList.add('hide');
  }

  // Calls fn once, when the video has actually drawn a frame.
  function onFirstFrame(video, fn) {
    if (typeof video.requestVideoFrameCallback === 'function') {
      video.requestVideoFrameCallback(function () { fn(); });
    } else {
      video.addEventListener('playing', function () { fn(); }, { once: true });
    }
  }

  // Build (once per slide) the muted clone that plays inside the slide.
  function getClone(slide) {
    var existing = clones.get(slide);
    if (existing) return existing;

    var original = slide.querySelector('video.product-video-mp4-sound');
    var mediaBox = slide.querySelector('.product-image-main');
    if (!original || !mediaBox) return null;

    // Clone rather than reuse the original element, same approach already
    // proven to start this video reliably.
    var clone = original.cloneNode(true);
    clone.removeAttribute('id');
    clone.removeAttribute('controls');
    clone.removeAttribute('poster'); // the sharp still image underneath is better
    clone.classList.remove('hide');
    clone.muted = true;
    clone.defaultMuted = true;
    clone.setAttribute('muted', '');
    clone.setAttribute('playsinline', '');
    clone.loop = true;
    clone.preload = 'auto';
    clone.style.position = 'absolute';
    clone.style.top = '0';
    clone.style.left = '0';
    clone.style.width = '100%';
    clone.style.height = '100%';
    clone.style.objectFit = 'contain';
    clone.style.zIndex = '2';
    clone.style.pointerEvents = 'none';
    clone.style.opacity = '0';
    clone.style.transition = 'opacity 0.2s ease';

    // Use the small 480p MP4 (about 1/3 of the 720p file) on mobile. If it is
    // missing or fails to load, fall back to the theme's original sources.
    var originalSources = Array.prototype.slice.call(
      clone.querySelectorAll('source')
    ).map(function (s) { return s.getAttribute('src'); });
    var hdUrl = originalSources.filter(function (u) {
      return u && u.indexOf('HD-720p-4.5Mbps') !== -1;
    })[0];
    if (hdUrl) {
      clone.innerHTML = '';
      clone.addEventListener('error', function () {
        clone.innerHTML = '';
        originalSources.forEach(function (u) {
          var s = document.createElement('source');
          s.src = u;
          clone.appendChild(s);
        });
        clone.load();
        if (slide.classList.contains('is-selected')) {
          clone.play().catch(function () {});
        }
      }, { once: true });
      clone.src = hdUrl.replace('HD-720p-4.5Mbps', 'SD-480p-1.5Mbps');
    }

    mediaBox.appendChild(clone);
    clones.set(slide, clone);
    return clone;
  }

  function removeClone(slide) {
    var clone = clones.get(slide);
    if (!clone) return;
    clone.pause();
    if (clone.parentNode) clone.parentNode.removeChild(clone);
    clones.delete(slide);
  }

  // Load the video in the background while its slide is not visible: play it
  // muted just until the first frame is drawn, then pause. The browser keeps
  // downloading the (small) file, so it is ready when the shopper swipes.
  function warmUp(slide) {
    if (!isMobileWidth() || slide.classList.contains('is-selected')) return;
    var clone = getClone(slide);
    if (!clone) return;
    onFirstFrame(clone, function () {
      if (!slide.classList.contains('is-selected')) {
        clone.pause();
        try { clone.currentTime = 0; } catch (e) { /* not seekable yet */ }
      }
    });
    clone.play().catch(function () { /* background load is best effort */ });
  }

  function startVideo(slide) {
    if (!isMobileWidth()) return;
    hideTrigger(slide);
    var clone = getClone(slide);
    if (!clone) return;

    // Keep the video hidden until it has drawn a frame, then fade it in.
    clone.style.opacity = '0';
    try { clone.currentTime = 0; } catch (e) { /* not seekable yet */ }
    onFirstFrame(clone, function () {
      if (slide.classList.contains('is-selected')) clone.style.opacity = '1';
    });
    clone.play().catch(function () {
      /* nothing to do - a muted play() call is never blocked in practice,
         but ignore silently if a browser ever refuses it. */
    });
  }

  function stopVideo(slide) {
    var clone = clones.get(slide);
    if (!clone) return;
    clone.pause();
    clone.style.opacity = '0';
  }

  function handleSlideChange(slide) {
    if (slide.classList.contains('is-selected')) {
      startVideo(slide);
    } else {
      stopVideo(slide);
    }
  }

  function init() {
    var slides = document.querySelectorAll('.product-main-slide');
    if (!slides.length) return;

    slides.forEach(function (slide) {
      // No tap-for-sound prompt on mobile - autoplay muted instead.
      hideTrigger(slide);
      handleSlideChange(slide);
    });

    // Flickity marks the active slide by toggling "is-selected" on it as
    // the shopper swipes; watch for that instead of re-implementing slide
    // tracking ourselves.
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.type === 'attributes' && m.attributeName === 'class') {
          handleSlideChange(m.target);
        }
      });
    });
    slides.forEach(function (slide) {
      observer.observe(slide, { attributes: true, attributeFilter: ['class'] });
    });

    window.addEventListener('resize', function () {
      slides.forEach(function (slide) {
        if (isMobileWidth()) {
          handleSlideChange(slide);
        } else {
          removeClone(slide);
        }
      });
    });

    // Pre-load the video in the background once the page itself is done.
    function warmAll() {
      slides.forEach(warmUp);
    }
    function scheduleWarm() {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(warmAll, { timeout: 2000 });
      } else {
        setTimeout(warmAll, 500);
      }
    }
    if (document.readyState === 'complete') {
      scheduleWarm();
    } else {
      window.addEventListener('load', scheduleWarm);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Belt-and-braces: if a shopper ever taps the trigger before our script
  // hides it (e.g. a slow connection), still block the theme's fullscreen
  // sound modal from opening rather than letting the old jump happen.
  document.addEventListener('click', function (evt) {
    var trigger = evt.target.closest && evt.target.closest('.product-video-trigger--mp4');
    if (!trigger) return;
    evt.preventDefault();
    evt.stopImmediatePropagation();
  }, true);
})();